import { useEffect, useMemo, useState } from 'react';
import { getCommunitySponsors, isApiError } from '@/lib/api';
import type { CommunitySponsorSummary } from '@/types/community';

const FALLBACK_ERROR = "Couldn't load sponsors. Try again.";
const NOT_FOUND_MESSAGE = 'No results';

type CommunitySearchProps = {
  onResults: (sponsors: CommunitySponsorSummary[], query: string) => void;
  onStateChange?: (state: { query: string; loading: boolean; error: string | null; notFound: boolean }) => void;
  initialQuery?: string;
  debounceMs?: number;
};

export default function CommunitySearch({
  onResults,
  onStateChange,
  initialQuery = '',
  debounceMs = 300,
}: CommunitySearchProps) {
  const [value, setValue] = useState(initialQuery);
  const [status, setStatus] = useState<{ type: 'idle' | 'empty' | 'error'; message: string | null }>({
    type: 'idle',
    message: null,
  });
  useEffect(() => {
    if (!onResults) return;
    const query = value.trim();
    if (!query) {
      onResults([], '');
      if (onStateChange) {
        onStateChange({ query: '', loading: false, error: null, notFound: false });
      }
      setStatus({ type: 'idle', message: null });
      return;
    }

    let aborted = false;
    const controller = new AbortController();
    const handle = window.setTimeout(async () => {
      if (onStateChange) {
        onStateChange({ query, loading: true, error: null, notFound: false });
      }
      setStatus({ type: 'idle', message: null });
      try {
        const sponsors = await getCommunitySponsors(query, { signal: controller.signal, limit: 12 });
        if (!aborted) {
          onResults(sponsors, query);
          if (onStateChange) {
            onStateChange({ query, loading: false, error: null, notFound: false });
          }
          setStatus({ type: 'idle', message: null });
        }
      } catch (err) {
        if (aborted) return;
        console.error('community search error', err);
        if (isApiError(err) && err.status === 404) {
          onResults([], query);
          if (onStateChange) {
            onStateChange({ query, loading: false, error: NOT_FOUND_MESSAGE, notFound: true });
          }
          setStatus({ type: 'empty', message: NOT_FOUND_MESSAGE });
          return;
        }

        if (onStateChange) {
          onStateChange({ query, loading: false, error: FALLBACK_ERROR, notFound: false });
        }
        setStatus({ type: 'error', message: FALLBACK_ERROR });
      }
    }, debounceMs);

    return () => {
      aborted = true;
      controller.abort();
      window.clearTimeout(handle);
    };
  }, [value, debounceMs, onResults, onStateChange]);

  const placeholder = useMemo(
    () => 'Search sponsors by name or handle…',
    [],
  );

  return (
    <div className="w-full max-w-xl">
      <label className="block text-sm font-semibold text-cocoa">Search sponsors</label>
      <input
        value={value}
        onChange={(event) => setValue(event.target.value)}
      placeholder={placeholder}
      className="mt-2 w-full rounded-xl border border-sand px-4 py-2 text-base text-cocoa shadow-inner focus:border-cocoa focus:outline-none"
      aria-label="Search sponsors"
    />
      {status.type === 'empty' && status.message ? (
        <p className="mt-2 text-sm text-cocoa/70">{status.message}</p>
      ) : null}
      {status.type === 'error' && status.message ? (
        <p className="mt-2 text-sm text-brick">{status.message}</p>
      ) : null}
    </div>
  );
}
